import { marked } from 'marked';
import { load as yamlLoad } from 'js-yaml';

export class StoryManager {
    constructor(app) {
        this.app = app;
        this.stories = {};
        this.currentChapter = 0;
        this.isLoading = true;
        
        // Load stories and create UI
        this.loadStories().then(() => {
            this.isLoading = false;
            this.createStoryWindow();
        }).catch(error => {
            console.error('Failed to load stories:', error);
            this.isLoading = false;
        });
    }
    
    async loadStories() {
        const storyIds = ['briolissens', 'dunyawi'];
        let hasLoadedAnyStory = false;
        
        for (const storyId of storyIds) {
            try {
                const response = await fetch(`/content/stories/${storyId}/story.yaml`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const yamlContent = await response.text();
                const storyData = yamlLoad(yamlContent);
                console.log('Loaded story data:', storyData);
                
                if (!storyData.title || !storyData.chapters) {
                    throw new Error('Invalid story data: missing title or chapters');
                }
                
                this.stories[storyId] = {
                    title: storyData.title,
                    description: storyData.description || '',
                    chapters: storyData.chapters
                };
                hasLoadedAnyStory = true;
            } catch (error) {
                console.error(`Error loading story ${storyId}:`, error);
                // Initialize with empty data to prevent null reference errors
                this.stories[storyId] = {
                    title: 'Error',
                    description: 'Failed to load story',
                    chapters: []
                };
            }
        }
        
        if (!hasLoadedAnyStory) {
            throw new Error('Failed to load any stories');
        }
    }

    createStoryWindow() {
        // Remove existing story window if it exists
        if (this.storyWindow) {
            this.storyWindow.remove();
        }

        this.storyWindow = document.createElement('div');
        this.storyWindow.className = 'story-window';
        document.body.appendChild(this.storyWindow);
    }

    toggleSidebar() {
        const chapterList = this.storyWindow.querySelector('.chapter-list');
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            chapterList.classList.toggle('visible');
        } else {
            chapterList.classList.toggle('collapsed');
        }

        const button = chapterList.querySelector('.toggle-sidebar');
        if (button) {
            button.textContent = chapterList.classList.contains('collapsed') ? '▶' : '◀';
        }
    }

    showStory(storyId, options = {}) {
        if (this.isLoading) {
            console.log('Stories still loading, please wait...');
            return;
        }

        const story = this.stories[storyId];
        if (!story) {
            console.error('Story not found:', storyId);
            return;
        }

        // Update URL unless specified not to
        const { updateHistory = true } = options;
        if (updateHistory) {
            history.pushState({}, '', `/stories/${storyId}`);
        }

        // Ensure story window exists
        if (!this.storyWindow) {
            this.createStoryWindow();
        }

        // Reset chapter when switching stories
        this.currentChapter = 0;

        // Clear previous content
        this.storyWindow.innerHTML = '';
        
        // Create main container
        const mainContainer = document.createElement('div');
        mainContainer.className = 'story-container';
        this.storyWindow.appendChild(mainContainer);

        // Create header
        const header = document.createElement('div');
        header.className = 'story-header';

        // Add story title to header
        const storyTitle = document.createElement('h1');
        storyTitle.className = 'story-title';
        storyTitle.textContent = story.title;
        header.appendChild(storyTitle);

        mainContainer.appendChild(header);

        // Create content wrapper for grid layout
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'story-content-wrapper';
        mainContainer.appendChild(contentWrapper);
        
        // Create chapter list
        const chapterList = document.createElement('div');
        chapterList.className = 'chapter-list';

        // Add toggle button to chapter list
        const toggleButton = document.createElement('button');
        toggleButton.className = 'toggle-sidebar';
        toggleButton.textContent = '◀';
        toggleButton.onclick = () => this.toggleSidebar();
        chapterList.appendChild(toggleButton);
        
        // Create chapter list
        story.chapters.forEach((chapter, index) => {
            const chapterItem = document.createElement('div');
            chapterItem.className = 'chapter-item';
            
            const chapterTitle = document.createElement('div');
            chapterTitle.className = 'chapter-title';
            chapterTitle.textContent = chapter.title;
            chapterTitle.onclick = () => this.showChapter(storyId, index);
            
            chapterItem.appendChild(chapterTitle);
            chapterList.appendChild(chapterItem);
        });
        
        // Add chapter list to content wrapper
        contentWrapper.appendChild(chapterList);
        
        // Create content area
        const contentArea = document.createElement('div');
        contentArea.className = 'story-content';
        contentWrapper.appendChild(contentArea);
        
        // Create title container
        const titleContainer = document.createElement('div');
        titleContainer.className = 'story-header';
        const titleElement = document.createElement('h1');
        titleElement.textContent = story.title;
        titleContainer.appendChild(titleElement);
        
        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.className = 'story-content';
        
        // Create chapter navigation
        const chapterNav = document.createElement('div');
        chapterNav.className = 'chapter-nav';
        
        // Add elements to content area
        contentArea.appendChild(titleContainer);
        contentArea.appendChild(contentContainer);
        contentArea.appendChild(chapterNav);
        
        this.storyWindow.classList.add('visible');
        
        // Create toggle and add it to the title
        const toggle = this.app.viewManager.createToggleSwitch(this.storyWindow);
        storyTitle.appendChild(toggle);
        
        // Show toggle and ensure content is visible
        this.app.viewManager.showToggle();
        
        // Show initial chapter
        this.showChapter(storyId, this.currentChapter);
    }

    async showChapter(storyId, chapterIndex, options = {}) {
        const story = this.stories[storyId];
        if (!story || !story.chapters[chapterIndex]) return;

        this.currentChapter = chapterIndex;
        const chapter = story.chapters[chapterIndex];

        // Update URL unless specified not to
        const { updateHistory = true } = options;
        if (updateHistory) {
            const chapter = story.chapters[chapterIndex];
            console.log('Chapter data:', chapter);
            const chapterSlug = chapter.slug;
            console.log('Chapter slug:', chapterSlug);
            history.pushState({}, '', `/stories/${storyId}/${chapterSlug}`);
        }
        
        // Load chapter content from file
        try {
            const response = await fetch(`/content/stories/${storyId}/${chapter.path}`);
            const chapterData = yamlLoad(await response.text());
            const contentHtml = marked.parse(chapterData.content);
            
            const contentContainer = this.storyWindow.querySelector('.story-content');
            if (contentContainer) {
                contentContainer.innerHTML = contentHtml;
                // Remove the original h1 from content if it exists
                const contentH1 = contentContainer.querySelector('h1');
                if (contentH1) contentH1.remove();
            }
        } catch (error) {
            console.error('Error loading chapter:', error);
            const contentContainer = this.storyWindow.querySelector('.story-content');
            if (contentContainer) {
                contentContainer.innerHTML = '<h2>Error</h2><p>Failed to load chapter content.</p>';
            }
        }

        // Update active chapter in sidebar
        const allChapters = this.storyWindow.querySelectorAll('.chapter-item');
        allChapters.forEach((item, index) => {
            if (index === chapterIndex) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });

        // Update navigation
        const chapterNav = this.storyWindow.querySelector('.chapter-nav');
        if (chapterNav) {
            chapterNav.innerHTML = '';
            
            if (this.currentChapter > 0) {
                const prevButton = document.createElement('button');
                prevButton.textContent = '← Previous';
                prevButton.onclick = () => this.showChapter(storyId, this.currentChapter - 1);
                chapterNav.appendChild(prevButton);
            }
            
            if (this.currentChapter < story.chapters.length - 1) {
                const nextButton = document.createElement('button');
                nextButton.textContent = 'Next →';
                nextButton.onclick = () => this.showChapter(storyId, this.currentChapter + 1);
                chapterNav.appendChild(nextButton);
            }
        }
    }

    updateActiveChapter(chapterId, subchapterId = null) {
        // Remove active class from all items
        const allItems = this.storyWindow.querySelectorAll('.chapter-item, .subchapter-item');
        allItems.forEach(item => item.classList.remove('active'));

        // Add active class to current chapter/subchapter
        if (subchapterId) {
            const subItem = this.storyWindow.querySelector(`.subchapter-item[data-id="${subchapterId}"]`);
            if (subItem) subItem.classList.add('active');
        } else {
            const chapterItem = this.storyWindow.querySelector(`.chapter-item[data-id="${chapterId}"]`);
            if (chapterItem) chapterItem.classList.add('active');
        }
    }

    hideStory() {
        // Check if viewManager and storyWindow exist
        if (this.app.viewManager && this.app.viewManager.toggleInput) {
            this.app.viewManager.toggleInput.checked = false;
        }
        
        if (this.storyWindow) {
            this.storyWindow.classList.remove('visible');
        }
        
        if (this.app.viewManager) {
            this.app.viewManager.hideToggle();
        }
    }
}
